/*
 * The brand mark and lockup (direction §8).
 *
 * WHY THIS IS A COMPONENT AND NOT AN <img src>. The mark is drawn in MODE PAIRS
 * — charcoal/red on light, warm off-white/bright red on dark — with two separate
 * SVG masters. A single src is a bug that only shows in one theme, which is
 * exactly how it was found: the Phase 1 scaffold hard-coded the light lockup and
 * it was invisible against the dark ground.
 *
 * WHY NOT A CSS FILTER. `filter: invert()` on a logo produces a different logo:
 * the cherry bar would inverted to cyan-green, which is the one colour in this
 * system that carries meaning. §8's mode pair exists because the two are drawn,
 * not computed.
 *
 * WHY NOT <picture> WITH prefers-color-scheme. That follows the OS only, and the
 * theme here is a user preference held on the server (contract §2) which can
 * override it in either direction. So both images render and CSS picks, keyed on
 * exactly the selectors tokens.css uses — `[data-theme]` first, the media query
 * as the fallback for "System". One rule set, so the logo cannot disagree with
 * the palette.
 *
 * THE ANIMATION plays ONCE at sign-in and cold launch only (§8): a curtain
 * raiser, never a loading gate, never on tab navigation. It NEVER gates content,
 * so callers render it beside content that is already on screen. Reduced motion
 * — OS or the in-app toggle — shows the static mark instantly.
 */

import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import styles from "./Brand.module.css";

/*
 * Whether motion should be suppressed, from EITHER source.
 *
 * §6 requires the in-app toggle to be honoured alongside `prefers-reduced-motion`
 * rather than instead of it, so this is an OR: the OS media query, or the
 * `data-reduce-motion` attribute main.tsx sets from the server-held preference.
 *
 * WHY THE COMPONENT ASKS INSTEAD OF LEAVING IT TO CSS. The animated master
 * contains its own `@media (prefers-reduced-motion: reduce) { animation: none }`,
 * which sounds sufficient and is not: with the animation disabled, the third bar
 * keeps its PRE-ANIMATION state, which is off-stage and invisible. So reduced
 * motion produced a two-bar logo. §8 says reduced motion "shows the static mark",
 * and the only way to guarantee that is to render the static file.
 *
 * useSyncExternalStore rather than an effect: it subscribes without a render pass
 * where the wrong thing is briefly on screen, and it re-reads if the user changes
 * the OS setting while the app is open.
 */
function useReducedMotion(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      mq.addEventListener("change", onChange);

      // The in-app toggle changes an attribute, which a media query cannot see.
      const observer = new MutationObserver(onChange);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-reduce-motion"],
      });

      return () => {
        mq.removeEventListener("change", onChange);
        observer.disconnect();
      };
    },
    () =>
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      document.documentElement.dataset["reduceMotion"] === "true",
    // Server snapshot: no motion. There is no SSR here, but the conservative
    // answer is the one that never animates something a user asked not to.
    () => true,
  );
}

export interface BrandProps {
  /**
   * "mark" is the three bars alone, which §8 requires to work at ~20px as the
   * nav glyph. "lockup" is the mark plus the wordmark.
   */
  variant?: "mark" | "lockup";

  /** Rendered height in px. The SVGs scale; the aspect ratio is preserved. */
  height?: number;

  /**
   * Play the "late to the strip" animation once. Only true at sign-in and cold
   * launch (§8). Honours `prefers-reduced-motion` and the in-app motion toggle
   * automatically, both of which resolve to the static mark.
   */
  animated?: boolean;

  /**
   * Size to the CONTAINER's width rather than to `height`, preserving the aspect
   * ratio. For the one place the logo is the largest thing on screen rather than
   * a fixture in a bar: sign-in, where §12 puts it above the form and it reads as
   * an identity rather than as chrome. `height` is ignored when this is set.
   */
  fluid?: boolean;
}

export function Brand({ variant = "lockup", height = 28, animated = false, fluid = false }: BrandProps) {
  const { t } = useTranslation();
  const alt = t("brand.markAlt");
  const reducedMotion = useReducedMotion();
  const wrapClass = fluid ? `${styles.wrap} ${styles.fluid}` : styles.wrap;

  /*
   * BOTH variants animate now. The mark-only master — viewBox "-20 0 46 24",
   * three rects, no wordmark — used to be the only animated asset, so an animated
   * LOCKUP was not producible and asking for one fell back to the static lockup
   * rather than silently dropping the wordmark. Deviation #8 recorded that trade
   * and named its fix: an animated lockup master. That file now exists, carrying
   * the static lockup's geometry exactly, so there is nothing left to trade —
   * the bars play their entrance while the name is readable from the first frame.
   *
   * Reduced motion still resolves to the static pair, for the reason the hook
   * above documents: the in-file `animation: none` leaves a bar off-stage.
   */
  const canAnimate = animated && !reducedMotion;

  /*
   * EVERY PATH RENDERS A MODE PAIR, animated included, and that uniformity is the
   * fix for a real bug rather than tidiness.
   *
   * The animated masters used to be single files carrying both palettes behind
   * `@media (prefers-color-scheme: dark)`. That follows the OPERATING SYSTEM, and
   * this app's theme is a user preference held on the server (contract §2) which
   * can disagree with the OS in either direction — the very reason the static
   * masters are a pair and the header above rejects `<picture>`. An `<img>`-
   * embedded SVG cannot see `[data-theme]`, so it cannot be told.
   *
   * Result: OS light plus app dark rendered the LIGHT palette on a dark ground,
   * which is unreadable rather than merely wrong. It survived review because the
   * dark-mode check set the BROWSER's colour scheme, which makes the OS and the
   * app agree and hides exactly this case.
   */
  const base = canAnimate
    ? variant === "mark"
      ? "thirdcall-logo-animated"
      : "thirdcall-lockup-animated"
    : variant === "mark"
      ? "thirdcall-mark"
      : "thirdcall-lockup";

  const sizing = fluid ? {} : { height };
  const imgClass = (mode: "light" | "dark") => {
    const pair = mode === "light" ? styles.light : styles.dark;
    if (!canAnimate) return pair;
    // The animated files carry the padded viewBox, so they need the size
    // correction as well as the pair rule.
    return `${pair} ${fluid ? styles.animatedFluid : styles.animated}`;
  };

  return (
    <span className={wrapClass} style={fluid ? undefined : { blockSize: height }}>
      {/*
        Both masters render; CSS shows exactly one. `alt` on the visible one only
        would require knowing which is visible, so the pair is wrapped in a
        single labelled element and both images are decorative to assistive
        technology — the label lives on the wrapper.
      */}
      <span role="img" aria-label={alt} className={fluid ? styles.pair : undefined}>
        <img
          className={imgClass("light")}
          src={`/brand/${base}-light.svg`}
          alt=""
          aria-hidden="true"
          {...sizing}
        />
        <img
          className={imgClass("dark")}
          src={`/brand/${base}-dark.svg`}
          alt=""
          aria-hidden="true"
          {...sizing}
        />
      </span>
    </span>
  );
}
